import { useInfiniteScrollTable } from "@flaminem/ui-components";
import { FC, Fragment, useCallback, useState } from "react";
import { useNavigate } from "react-router";

import { DimensionedLayout } from "src/components/DimensionedLayout/DimensionedLayout.tsx";
import { useDimensions } from "src/components/DimensionedLayout/useDimensions.ts";
import { InfiniteScrollTable } from "src/components/Tables/InfiniteScrollTable.tsx";
import { defaultRowHeight } from "src/config/table-config.ts";
import { useGetBlacklistElementsQuery } from "src/graphql/anti-fraud/queries/blacklists/BlacklistElementsQuery.ts";
import { useListSchema } from "src/pages/Blacklists/useListSchema.ts";
import { BlacklistElement } from "src/types/blacklist.ts";

import { BlacklistsTableToolbar } from "./BlacklistsTable/BlacklistsTableToolbar.tsx";
import { useBlacklistsTableURLParams } from "./BlacklistsTable/useBlacklistsTableURLParams";
import { useGetBlacklistsTableColumns } from "./BlacklistsTable/useGetBlacklistsTableColumns.tsx";

const BlacklistTab: FC = () => {
  const [tableKey, setTableKey] = useState(0);
  const { pageSize, dimensions } = useDimensions();
  const { listSchema } = useListSchema();
  const navigate = useNavigate();

  const { includeDisabledElements, handleOnFiltersChange, searchTerm, handleOnSearchTermChange } =
    useBlacklistsTableURLParams();

  const {
    data: blacklistElements,
    error,
    hasError,
    loading,
    refetch,
  } = useGetBlacklistElementsQuery({
    blacklistCode: listSchema?.code,
    includeDisabledElements,
    pagination: { offset: 0, limit: pageSize },
    searchTerm,
  });

  const { data } = useInfiniteScrollTable<BlacklistElement>(
    blacklistElements?.blacklistElements ?? [],
    blacklistElements?.pageInfo.totalCount ?? 0,
    pageSize,
    blacklistElements?.pageInfo.offset ?? 0
  );

  const loadMore = useCallback(
    (startIndex: number, stopIndex: number) => {
      if (!loading) {
        refetch({
          blacklistCode: listSchema?.code,
          includeDisabledElements,
          pagination: {
            limit: stopIndex - startIndex + 1,
            offset: startIndex,
          },
          searchString: searchTerm || null,
        });
      }
    },
    [includeDisabledElements, listSchema?.code, loading, refetch, searchTerm]
  );

  const refreshTable = useCallback(() => {
    // navigate to the same tab will refresh all the data, this fixes FLAM-5400,FLAM-5411 and FLAM-5412
    setTableKey((prevKey) => prevKey + 1); // Change the key to force remount
  }, []);

  const refreshData = () => {
    refreshTable();
  };

  const columns = useGetBlacklistsTableColumns({
    onStatusChanged: refreshData,
    onElementUpdated: refreshData,
    searchTerm,
  });

  return (
    <Fragment key={tableKey}>
      <DimensionedLayout>
        {dimensions?.width && (
          <InfiniteScrollTable
            key={tableKey}
            id={`blacklists-table-${listSchema?.code}`}
            totalCount={data.length ?? 0}
            rowHeight={defaultRowHeight}
            fetchMore={loadMore}
            data={data ?? []}
            isLoading={loading}
            columns={columns}
            pageSize={pageSize}
            onRowClick={(row) => {
              navigate(`/blacklist/${row.data?.blacklist.code}/${row.data?.id}`);
            }}
            toolbar={() => (
              <BlacklistsTableToolbar
                filterValues={{ includeDisabledElements }}
                handleOnFiltersChange={handleOnFiltersChange}
                onElementCreated={refreshData}
                searchTerm={searchTerm}
                handleOnSearchTermChange={handleOnSearchTermChange}
              />
            )}
            errorText={error?.message}
            isError={hasError}
          />
        )}
      </DimensionedLayout>
    </Fragment>
  );
};

export default BlacklistTab;
